import { Box, Flex, Spacer, Text } from '@chakra-ui/react';
import React, { useEffect, useState } from 'react';

import { Util } from '@finley-technologies/finley-types';

import { BorrowingBaseSummaryData } from './types';
import { colors, transparency } from '~/packages/common-frontend/colors';
import { getDefaultDashboardDisplayEndDate } from '~/packages/common-frontend/constants';
import { PortfolioChartCards } from '~/packages/portfolio/ui/shared/PortfolioChartCards';
import {
  ChartTimePeriod,
  formatCsvDownloadDataRows,
  getInitialActiveFields,
  onToggleActiveField,
  PortfolioChartConfig,
} from '~/packages/portfolio/ui/shared/portfolioChartUtils';
import { PortfolioDownloadCsvLink } from '~/packages/portfolio/ui/shared/PortfolioDownloadCsvLink';
import { PortfolioReChart } from '~/packages/portfolio/ui/shared/PortfolioReChart';

export type BorrowingBaseSummaryDataKeys = keyof BorrowingBaseSummaryData;

// TODO: @dmads98 reconcile with borrowingBaseReceivablesChartConfig
export const chartConfig: PortfolioChartConfig<BorrowingBaseSummaryDataKeys> = {
  receivableExposure: {
    chartType: 'area',
    field: 'receivableExposure',
    label: 'Receivable Exposure',
    format: 'currency',
    color: `${colors.chart.lilac}${transparency[75]}`,
  },
  eligibleExposure: {
    chartType: 'area',
    field: 'eligibleExposure',
    label: 'Eligible Exposure',
    format: 'currency',
    color: `${colors.chart.mint}${transparency[75]}`,
  },
  eligibleExposureLessConcentrationKickouts: {
    chartType: 'area',
    field: 'eligibleExposureLessConcentrationKickouts',
    label: 'Eligible Exposure Less Concentration',
    format: 'currency',
    color: `${colors.chart.cream}${transparency[75]}`,
  },
  borrowingBaseAmount: {
    chartType: 'area',
    field: 'borrowingBaseAmount',
    label: 'Borrowing Base Amount',
    format: 'currency',
    color: `${colors.chart.sky}${transparency[75]}`,
  },
  advanceRate: {
    chartType: 'dashedLine',
    field: 'advanceRate',
    label: 'Advance Rate',
    format: 'percentage',
    color: colors.black[95],
  },
};

type BorrowingBaseSummaryChartProps = {
  historicalRecords: any[];
  selectedTimePeriod: ChartTimePeriod;
};
export const BorrowingBaseSummaryChart = ({
  historicalRecords,
  selectedTimePeriod,
}: BorrowingBaseSummaryChartProps) => {
  const chartTitle = 'Borrowing Base Summary';
  const [data, setData] = useState<BorrowingBaseSummaryData[]>([]);

  const initialActiveFields = getInitialActiveFields({
    chartConfig,
  }) as BorrowingBaseSummaryDataKeys[];

  const [activeFields, setActiveFields] = useState<
    BorrowingBaseSummaryDataKeys[]
  >(initialActiveFields);

  const [
    selectedBar,
    setSelectedBar,
  ] = useState<BorrowingBaseSummaryData | null>(null);

  useEffect(() => {
    const graphData: BorrowingBaseSummaryData[] = historicalRecords.map(
      (record) => {
        return {
          reportingDate: record.reportingdate as Util.FullDateStr,
          receivableExposure: record['rif.exposure'],
          eligibleExposure: record['ef.eligibleexposure'],
          eligibleExposureLessConcentrationKickouts:
            record['clf.eligibleexposurelessconcentrationlimitkickouts'],
          advanceRate: record['bbf.advancerate'],
          borrowingBaseAmount: record['bbf.borrowingbaseamount'],
        };
      },
    );
    if (graphData.length > 0) {
      setSelectedBar(graphData[graphData.length - 1]);
    }
    setData(graphData);
  }, [historicalRecords]);

  const {
    dataRows,
    headers,
  } = formatCsvDownloadDataRows<BorrowingBaseSummaryData>(data, activeFields);

  return (
    <Box
      backgroundColor={colors.background[50]}
      mt="40px"
      border="none"
      borderRadius="8px"
      padding="40px"
    >
      <Flex>
        <Text fontSize="2xl" fontWeight="semibold" mb="40px">
          Borrowing Base Summary
        </Text>
      </Flex>
      <Flex padding="40px" background="white" borderRadius="8px" border="none">
        <Box width="80%">
          <PortfolioReChart
            chartData={data}
            selectedTimePeriod={selectedTimePeriod}
            currentSelectedBarReportingDate={
              selectedBar
                ? selectedBar.reportingDate
                : getDefaultDashboardDisplayEndDate()
            }
            onSelectBar={setSelectedBar}
            chartConfig={chartConfig}
            visibleChartComponents={activeFields}
            tooltipLabelType="date"
          />
        </Box>

        <Spacer />

        {selectedBar && (
          <Flex width="25%" flexDirection="column">
            <PortfolioChartCards
              selectedBarData={selectedBar}
              chartConfig={chartConfig}
              activeFields={activeFields}
              onToggleActiveField={(selectedField) =>
                onToggleActiveField({
                  selectedField,
                  currentActiveFields: activeFields,
                  setActiveFields,
                })
              }
            />
            <Spacer />

            <PortfolioDownloadCsvLink
              dataRows={dataRows}
              headers={headers}
              chartTitle={chartTitle}
              selectedTimePeriod={selectedTimePeriod}
            />
          </Flex>
        )}
      </Flex>
    </Box>
  );
};
